import React, { FC, useState } from 'react'
import styled from 'styled-components'
import Button from '../../../ui/Button/Button'
import Text from '../../../ui/Text/Text'
import { SearchIcon, FilterIcon, PlusIcon, GreenXIcon, BlackPointUpArrowIcon, ShareIcon, WhatsappIcon } from './icons'
import { SearchAndFilterProps, Coupon } from './types'
import { SearchInput as SharedSearchInput, CategoryChip, FlexSpaceBetween } from './SharedStyledComponents'

const SearchAndFilterContainer = styled.div`
 display: flex;
padding-top: 16px;
flex-direction: column;
align-items: center;
gap: 16px;
padding-inline: 12px;
width: 100%;
`

const SearchAndTitleLine = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  @media (max-width: 1081px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`

const SearchContainer = styled.div`
display: flex;
width: 612px;
padding: 8px 16px;
justify-content: space-between;
align-items: center;
flex-shrink: 0;
border-radius: 100px;
background: var(--Neutral-Surface-Minimal, #FFF);
position: relative;
@media (max-width: 1081px) {
  width: 100%;
}
`

const SearchInputWrapper = styled.div`
  width: 400px;
  position: relative;
  display: flex;
  align-items: center;
  @media (max-width: 1081px) {
    width: 100%;
  }
`

const SearchInput = styled(SharedSearchInput)`
  position: relative;
  z-index: 2;
  font-family: inherit;
  line-height: normal;
  direction: rtl;
  text-align: right;

  &::placeholder {
    color: #B0B0B0;
    font-size: 16px;
  }
`

const AutocompleteText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-right: 1.8px;
  font-size: 14px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 1;
  color: transparent;
  font-family: inherit;
  line-height: normal;
  display: flex;
  align-items: center;
  direction: rtl;
  text-align: right;
  overflow: hidden;

  @media (max-width: 1081px) {
    max-width: 200px;
  }
`

const AutocompleteSuggestion = styled.span`
  color: #B0B0B0;
`


const AddButton = styled(Button)<{ hideOnMobile?: boolean }>`
display: flex;
padding: 16px;
justify-content: center;
align-items: center;
gap: 8px;
background: var(--Brand-Surface-Main, #007EFF);
color: var(--Neutral-Text-Inverse-Default, #FFF);
@media (max-width: 1081px) {
  position: fixed;
  bottom: 72px;
  z-index: 1000;
  ${({ hideOnMobile }) => hideOnMobile && 'display: none;'}
}
`

const DropdownCategoryChip = CategoryChip

const FiltersLine = styled.div<{ $hidden?: boolean }>`
display: flex;
flex-wrap: wrap;
width: 99%;
padding: ${({ $hidden }) => ($hidden ? '0 16px' : '8px 16px')};
gap: 20px;
align-items: center;
flex-shrink: 0;
border-radius: 16px;
background: var(--Neutral-Surface-Minimal, #FFF);
overflow: hidden;
max-height: ${({ $hidden }) => ($hidden ? '0' : '300px')};
opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
transition: max-height 0.25s ease, opacity 0.2s ease, padding 0.25s ease;
@media (max-width: 1081px) {
  display: none;
}
`

const EmptyDropdown = styled.div`
position: absolute;
top: 63px;
left: 2px;
display: flex;
width 612px;
padding: 12px 16px;
justify-content: center;
align-items: center;
border-radius: 20px;
background: var(--Neutral-Surface-Minimal, #FFF);
z-index: 1000;
@media (max-width: 1081px) {
  width: 100%;
  left: 0;
}
`

const DropdownCategoriesContainer = styled.div`
display: flex;
justify-content: flex-start;
align-items: center;
align-content: center;
gap: 6px;
align-self: stretch;
flex-wrap: wrap;
`

const DropDownOptionsContainer = styled.div`
display: flex;
flex-direction: column;
align-items: flex-start;
gap: 8px;
align-self: stretch;
width: 100%;
`

const DropDownCategoryListing = styled.div<{ isSelected?: boolean }>`
display: flex;
height: 36px;
padding: 12px 1px;
border-bottom: 1px solid #D1D1D1;
flex-direction: column;
justify-content: center;
align-items: flex-start;
gap: 10px;
align-self: stretch;
cursor: pointer;
background: ${({ isSelected }) => isSelected ? '#E9ECEF' : 'transparent'};

&:hover {
  background: #E9ECEF;
}
 &:last-child {
  border-bottom: none;
 }


`

const GeneralAndLocalButtons = styled.div`
  display: flex;
  @media (max-width: 1081px) {
    width: 100%;
    padding: 0;
    box-sizing: border-box;
  }
`

const StyledFilterButton = styled(Button) <{ active?: boolean; side?: 'left' | 'right' }>`
  width: 120px;
  height: 44px;
  padding: 12px 16px 12px 12px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  background: ${({ active }) => active ? "#28A745" : "white"};
  border-top: 1px solid var(--Neutral-Borders-Minimal, #E7E7E7);
  border-bottom: 1px solid var(--Neutral-Borders-Minimal, #E7E7E7);

  ${({ side }) => side === 'right' ? `
    border-radius: 0 8px 8px 0;
    border-right: 1px solid var(--Neutral-Borders-Minimal, #E7E7E7);
  ` : `
    border-radius: 8px 0 0 8px;
    border-left: 1px solid var(--Neutral-Borders-Minimal, #E7E7E7);
  `}

  @media (max-width: 1081px) {
    width: 50%;

    &:hover {
      background: ${({ active }) => active ? "#28A745" : "white"} !important;
    }
  }
`

const TitleAndLocalGeneralAndLocalButtonsWrapper = styled.div`
display: flex;
padding: 0 12px;
align-items: center;
gap: 20px;
flex-direction: row-reverse;
@media (max-width: 1081px) {
  flex-direction: column-reverse;
  align-items: flex-end;
  width: 100%;
  gap: 10px;
  padding: 0;
}
`

const CategoryDropDownContainer = styled.div`
display: flex;
padding: 8px 10px;
flex-direction: column;
align-items: center;
border-radius: 10px;
background: var(--Neutral-Surface-Dark, #272727);
position: absolute;
top: 54px;
right: -40px;
z-index: 1000;
`

const CategoryDropDownItem = styled.div`
display: flex;
padding: 12px;
justify-content: center;
align-items: center;
gap: 8px;
border-top: 0.5px solid var(--Neutral-Borders-High, #5D5D5D);
white-space: nowrap;
 &:first-child {
    border-top: none;
  }
`

const WhatsappContainer = styled.div`
display: flex;
height: 60px;
margin-left: 4px;
padding: 16px;
justify-content: center;
align-items: center;
gap: 8px;
flex-shrink: 0;
align-self: stretch;
border-radius: 8px;
background: var(--Benefits-Surface-Light, #DCFCE4);
cursor: pointer;
@media (max-width: 1081px) {
  position: fixed;
  bottom: 72px;
  right: 190px;
  z-index: 1000;
}
`

const TitleWrapper = styled.div`
  @media (max-width: 1081px) {
    display: none;
  }
`

const DesktopOnlyButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 1081px) {
    display: none;
  }
`


import isMobile from 'utils/isMobile'

const SearchAndFilter: FC<SearchAndFilterProps> = ({ filterType, onFilterChange, coupons, floatingFilter, selectedCategories, onCategoryChange, searchValue, onSearchValueChange, onSearchChange, setIsFilterModalOpen, categories, usedAdvancedFilters, selectedAdvancedFiltersCategories, onAdvancedFiltersCategoriesChange, onClearAdvancedFilters, isShareButtonActive, setIsShareButtonActive, checkedCouponsCount, onShareClick, onCouponSelect, onWhatsappClick, isViewingCoupon, onOpenPublishModal, isPublishModalOpen, hideFiltersLine }) => {
  const [isTyping, setIsTyping] = React.useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = React.useState<number>(-1)
  const [isHoveringMoreCategories, setIsHoveringMoreCategories] = React.useState<boolean>(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // When parent clears the searchValue, also reset the dropdown / typing UI
  React.useEffect(() => {
    if (searchValue === '') {
      setIsTyping(false)
      setSelectedIndex(-1)
    }
  }, [searchValue])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onSearchValueChange(value)
    setSelectedIndex(-1) // Reset selection when typing

    // Only filter if there's an exact match with a coupon title
    const exactMatch = coupons.find(coupon =>
      coupon.title.toLowerCase() === value.toLowerCase()
    )

    if (exactMatch) {
      onSearchChange(value) // Notify parent only on exact match
    } else if (value.length === 0) {
      onSearchChange('') // Clear filter when search is empty
    }
  }

  const handleInputFocus = () => {
    setIsTyping(true)
  }

  const handleInputBlur = () => {
    // Add a small delay to allow click events to process
    setTimeout(() => {
      if (searchValue.length === 0) {
        setIsTyping(false)
      }
    }, 150)
  }

  const handleClearSearch = () => {
    if (usedAdvancedFilters) {
      // Clear advanced filters
      onClearAdvancedFilters()
    } else {
      // Clear search
      onSearchValueChange('')
      setIsTyping(false)
      setSelectedIndex(-1)
      onSearchChange('') // Notify parent to clear search
    }
  }

  // Get first matching suggestion for inline autocomplete
  const getAutocompleteSuggestion = (): string => {
    if (searchValue.length === 0) return ''

    const matchingCoupon = coupons.find(coupon =>
      coupon.title.toLowerCase().startsWith(searchValue.toLowerCase())
    )

    return matchingCoupon ? matchingCoupon.title : ''
  }

  const autocompleteSuggestion = getAutocompleteSuggestion()

  // Filter coupons based on search input for dropdown
  const filteredCoupons = coupons.filter(coupon =>
    coupon.title.toLowerCase().includes(searchValue.toLowerCase())
  ).slice(0, 4) // Limit to maximum 4 options

  const handleSelectSuggestion = (coupon: Coupon) => {
    onSearchValueChange('') // Clear search input
    setIsTyping(false)
    setSelectedIndex(-1)
    onSearchChange('') // Clear search query in parent
    // Navigate to coupon slug page
    if (onCouponSelect) {
      onCouponSelect(coupon.id)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle dropdown navigation when there are filtered coupons
    if (searchValue.length > 0 && filteredCoupons.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev =>
            prev < filteredCoupons.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < filteredCoupons.length) {
            handleSelectSuggestion(filteredCoupons[selectedIndex])
          }
          break
        case 'Tab':
        case 'ArrowRight':
          if (autocompleteSuggestion && selectedIndex === -1) {
            e.preventDefault()
            onSearchValueChange(autocompleteSuggestion)
            onSearchChange(autocompleteSuggestion)
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsTyping(false)
          setSelectedIndex(-1)
          inputRef.current?.blur()
          break
      }
    } else {
      // When no search results, only handle Tab/ArrowRight for inline autocomplete
      switch (e.key) {
        case 'Tab':
        case 'ArrowRight':
          if (autocompleteSuggestion && searchValue.length > 0) {
            e.preventDefault()
            onSearchValueChange(autocompleteSuggestion)
            onSearchChange(autocompleteSuggestion)
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsTyping(false)
          inputRef.current?.blur()
          break
      }
    }
  }



  const isMobileDevice = isMobile() ? 1 : 2

  return (
    <SearchAndFilterContainer>
      <SearchAndTitleLine>
        <TitleAndLocalGeneralAndLocalButtonsWrapper>
          {floatingFilter !== 'personal' && floatingFilter !== 'notifications' && floatingFilter !== 'liked' && (
            <GeneralAndLocalButtons>
              <StyledFilterButton
                onClick={() => onFilterChange('general')}
                active={filterType === 'general'}
                side="right"
              >
                <Text size="md" weight="medium" style={{ color: filterType === 'general' ? '#FFF' : '#272727' }}>כלליות</Text>
              </StyledFilterButton>
              <StyledFilterButton
                onClick={() => onFilterChange('local')}
                active={filterType === 'local'}
                side="left"
              >
                <Text size="md" weight="medium" style={{ color: filterType === 'local' ? '#FFF' : '#272727' }}>מקומיות</Text>
              </StyledFilterButton>
            </GeneralAndLocalButtons>
          )}
          <TitleWrapper>
            <Text weight="bold" style={{ fontSize: '40px' }}>{floatingFilter === 'liked' ? 'אהבתי' : floatingFilter === 'personal' ? 'איזור אישי' : floatingFilter === 'notifications' ? 'התראות' : 'הטבות'}</Text>
          </TitleWrapper>
        </TitleAndLocalGeneralAndLocalButtonsWrapper>
        {floatingFilter !== 'liked' && floatingFilter !== 'personal' && floatingFilter !== 'notifications' && (
          <SearchContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SearchIcon />

              <SearchInputWrapper>
                {!usedAdvancedFilters ? (

                  <>
                    <AutocompleteText>
                      {searchValue}
                      {autocompleteSuggestion && (
                        <AutocompleteSuggestion>
                          {autocompleteSuggestion.slice(searchValue.length)}
                        </AutocompleteSuggestion>
                      )}
                    </AutocompleteText>
                    <SearchInput
                      ref={inputRef}
                      placeholder="חיפוש מוצר או קטגוריה"
                      value={searchValue}
                      onChange={handleSearchChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      onKeyDown={handleKeyDown}
                    />
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {selectedAdvancedFiltersCategories.slice(0, isMobileDevice).map((category, index) => (
                      <CategoryChip
                        padding="8px 16px"
                        key={index}
                        border="none"
                        className={selectedAdvancedFiltersCategories.includes(category) ? 'active' : ''}
                        background={selectedAdvancedFiltersCategories.includes(category) ? '#F6F6F6' : 'transparent'}
                      >
                        <Text size="md" weight="medium" color='textPrimary' >{category}</Text>
                        <div onClick={(e) => {
                          e.stopPropagation()
                          // Remove this specific category
                          const updatedCategories = selectedAdvancedFiltersCategories.filter(cat => cat !== category)
                          // If no categories left, set to 'הכל'
                          onAdvancedFiltersCategoriesChange(updatedCategories)
                        }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <GreenXIcon />
                        </div>
                      </CategoryChip>
                    ))}
                    {selectedAdvancedFiltersCategories.length >
                      2 && (
                        <div
                          style={{ position: 'relative' }}
                          onMouseEnter={() => setIsHoveringMoreCategories(true)}
                          onMouseLeave={() => setIsHoveringMoreCategories(false)}
                        >
                          <CategoryChip
                            padding="8px 16px"
                            border="none"
                            className=""

                          >
                            <Text size="md" weight="medium" color='textPrimary' >{selectedAdvancedFiltersCategories.length - 2}+</Text>

                          </CategoryChip>
                          {isHoveringMoreCategories && (
                            <CategoryDropDownContainer>
                              {selectedAdvancedFiltersCategories.slice(2).map((category, index) => (
                                <CategoryDropDownItem key={index}>
                                  <Text size="md" weight="medium" style={{ color: 'white' }}>{category}</Text>
                                </CategoryDropDownItem>
                              ))}
                              <div style={{ position: 'absolute', top: -8.5, left: '50%', transform: 'translateX(-50%)' }}>
                                <BlackPointUpArrowIcon />
                              </div>
                            </CategoryDropDownContainer>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </SearchInputWrapper>

            </div>
            <FilterIcon isTyping={isTyping} onClear={handleClearSearch} onClick={() => setIsFilterModalOpen(true)} usedAdvancedFilters={usedAdvancedFilters} selectedAdvancedFiltersCategories={selectedAdvancedFiltersCategories} />
            {isTyping && searchValue.length >= 3 && (
              <EmptyDropdown
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
              >
                {filteredCoupons.length > 0 ? (
                  <DropDownOptionsContainer>
                    {filteredCoupons.map((coupon, index) => (
                      <DropDownCategoryListing
                        key={coupon.id}
                        isSelected={index === selectedIndex}
                        onClick={() => handleSelectSuggestion(coupon)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <Text size="md" weight="normal">
                          {coupon.title}
                        </Text>
                      </DropDownCategoryListing>
                    ))}
                  </DropDownOptionsContainer>
                ) : (
                  <div style={{ padding: '16px' }}>
                    <Text size="md" weight="medium" style={{ color: '#B0B0B0' }}>
                      לא נמצאו הטבות
                    </Text>
                  </div>
                )}
              </EmptyDropdown>
            )}

          </SearchContainer>
        )}

        {floatingFilter !== 'personal' && floatingFilter !== 'notifications' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {!isShareButtonActive ? (
              <AddButton onClick={() => onOpenPublishModal?.()} hideOnMobile={isPublishModalOpen}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PlusIcon />
                  <Text size="lg" weight="bold">אני רוצה לפרסם</Text>
                </div>
              </AddButton>
            ) : (
              <>
                <DesktopOnlyButtonsWrapper>
                  <AddButton onClick={onShareClick}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ShareIcon fill='white' />
                      <Text size="lg" weight="bold">שתף {checkedCouponsCount} הטבות</Text>
                    </div>
                  </AddButton>
                  <WhatsappContainer onClick={onWhatsappClick}>
                    <WhatsappIcon />
                  </WhatsappContainer>
                </DesktopOnlyButtonsWrapper>
              </>
            )}
          </div>
        )}

      </SearchAndTitleLine>
      {floatingFilter !== 'personal' && floatingFilter !== 'notifications' && floatingFilter !== 'liked' && !isViewingCoupon && (
        <FiltersLine $hidden={hideFiltersLine}>
          <DropdownCategoryChip
            className={!usedAdvancedFilters && selectedCategories.length === 0 ? 'active' : ''}
            onClick={() => {
              if (!usedAdvancedFilters && selectedCategories.length > 0) {
                // Clear all selected categories to show "הכל"
                selectedCategories.forEach(cat => onCategoryChange(cat))
              }
            }}
            style={{
              cursor: usedAdvancedFilters ? 'default' : 'pointer',
              opacity: usedAdvancedFilters ? 0.5 : 1
            }}
          >
            <Text size="md" weight="bold">הכל</Text>
          </DropdownCategoryChip>
          {categories.map((category, index) => (
            <DropdownCategoryChip
              key={index}
              className={!usedAdvancedFilters && selectedCategories.includes(category) ? 'active' : ''}
              onClick={() => {
                if (!usedAdvancedFilters) {
                  onCategoryChange(category)
                }
              }}
              style={{
                cursor: usedAdvancedFilters ? 'default' : 'pointer',
                opacity: usedAdvancedFilters ? 0.5 : 1
              }}
            >
              <Text size="md" weight="bold">{category}</Text>
            </DropdownCategoryChip>
          ))}
        </FiltersLine>
      )}
    </SearchAndFilterContainer>
  )
}

export default SearchAndFilter
